import type { ApiError, AriValueSource } from '@/shared/api';
import { Button } from '@/shared/components';
import { useTranslation } from 'react-i18next';
import { sourceLabel } from './calendarDisplay';

export type PricingOwnershipField = 'rate' | 'minimumStay';

interface Props {
  rateSource: AriValueSource | null;
  minimumStaySource: AriValueSource | null;
  onRelease?: (field: PricingOwnershipField) => void;
  releasingField?: PricingOwnershipField;
  releasePending?: boolean;
  releaseError?: ApiError;
}

export function DynamicPricingOwnershipSection({
  rateSource,
  minimumStaySource,
  onRelease,
  releasingField,
  releasePending = false,
  releaseError,
}: Props) {
  const { t } = useTranslation();
  if (!rateSource && !minimumStaySource) return null;
  return (
    <section
      className="border-line space-y-3 border-t pt-4"
      aria-label={t('admin.dynamicPricing.calendar.ownership.title')}
    >
      <h3 className="font-serif text-lg">{t('admin.dynamicPricing.calendar.ownership.title')}</h3>
      {rateSource && (
        <OwnershipRow
          label={t('admin.dynamicPricing.calendar.ownership.rate')}
          source={rateSource}
          releaseLabel={t('admin.dynamicPricing.calendar.ownership.releaseRate')}
          releasing={releasingField === 'rate'}
          disabled={releasePending || releasingField !== undefined}
          onRelease={onRelease ? () => onRelease('rate') : undefined}
        />
      )}
      {minimumStaySource && (
        <OwnershipRow
          label={t('admin.dynamicPricing.calendar.ownership.minimumStay')}
          source={minimumStaySource}
          releaseLabel={t('admin.dynamicPricing.calendar.ownership.releaseMinimumStay')}
          releasing={releasingField === 'minimumStay'}
          disabled={releasePending || releasingField !== undefined}
          onRelease={onRelease ? () => onRelease('minimumStay') : undefined}
        />
      )}
      {releaseError && (
        <p className="text-sealwax text-sm" role="alert">
          {releaseError.detail ?? releaseError.title}
        </p>
      )}
    </section>
  );
}

function OwnershipRow({
  label,
  source,
  releaseLabel,
  releasing,
  disabled,
  onRelease,
}: {
  label: string;
  source: AriValueSource;
  releaseLabel: string;
  releasing: boolean;
  disabled: boolean;
  onRelease?: () => void;
}) {
  return (
    <div className="bg-card-alt rounded-lg p-3">
      <strong className="block text-sm">
        {label} · {sourceLabel(source)}
      </strong>
      {source === 'Manual' && onRelease && (
        <Button
          className="mt-2 min-h-11 whitespace-normal"
          disabled={disabled}
          loading={releasing}
          onClick={onRelease}
        >
          {releaseLabel}
        </Button>
      )}
    </div>
  );
}
